"use client";

/**
 * The report's scrolling document host: a fixed rail on the left, one scroll
 * container on the right, and every section stacked inside it.
 *
 * Ported from the design source `compass-oppreport.jsx` in Claude Design
 * project `79357508-fe09-4122-831f-a0014c6a31b0` — `.rv`, `.rv-toc`,
 * `.rv-toclbl`, `.rv-tocitem` (+ `:hover`, `.on`), `.rv-scroll`, `.rv-body`,
 * `.rv-sec`, `.rv-h2`, `.rv-tail`, and the `@media (max-width:1080px)` rail
 * step-down.
 *
 * The design's `OppReport` renders only `.rv > .rv-scroll > .rv-body`; its
 * host owns the rail (plan §9 #1). This component owns both, because it is a
 * Storybook composition host rather than the app shell — so it keeps the
 * design's `flex: 0 0 188px` rail rather than Intake's 216px one. Phase 7
 * wires the same behaviour into `AssumptionsView`, whose rail stays 216px.
 *
 * `activeSectionId` is controlled: the scroll-spy reports upwards through
 * `onSelectSection` and the parent decides. Setting `activeSectionId` from
 * outside — a rail click, the Overview's "View all →" link — scrolls the
 * document to that section, exactly as changing `sidebarTab` does in the app
 * today.
 *
 * `locked` only adds the rail caption. It deliberately does not gate the
 * body: whether a locked report is reachable at all is the app's decision
 * (`Intake` already redirects locked report tabs to the assumptions tab),
 * and the Pending story has to be able to render the locked report to be
 * reviewable.
 */

import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import type { ReportSectionId, ReportSectionMeta } from "./report-types";

export interface ReportShellProps {
  sections: readonly ReportSectionMeta[];
  activeSectionId: ReportSectionId;
  onSelectSection: (id: ReportSectionId) => void;
  /** Rail caption when locked: "Complete the assumptions table to unlock the report." */
  locked: boolean;
  children: ReactNode;
}

/** Design copy for a report that cannot resolve its figures yet (plan §1). */
const LOCKED_CAPTION = "Complete the assumptions table to unlock the report.";

/** The attribute `ReportShellSection` stamps and the scroll-spy queries. */
const SECTION_ATTR = "data-report-section";

/**
 * The reading line, in px below the top of the scroll container: a section is
 * "current" once it reaches this line. The design uses the same idea with
 * `getBoundingClientRect().top + 90`.
 */
const SPY_TOP_INSET_PX = 76;

/**
 * How much of the container's bottom the spy band excludes, as a percentage.
 * Band = `SPY_TOP_INSET_PX` → 45% of the container height. Wide enough that
 * the 52px gap between two sections can never fall entirely inside it (so
 * something is always intersecting), narrow enough that it only ever contains
 * the section at the top of the viewport.
 */
const SPY_BOTTOM_INSET_PCT = 55;

/** Design `scrollToEl`: 340ms, cubic in-out, owned rather than native smooth. */
const SCROLL_DURATION_MS = 340;

/** Design `scrollToEl` lands each section 16px below the container top. */
const SECTION_TOP_GAP_PX = 16;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Design `scrollToEl`'s easing — `p<0.5 ? 4p³ : 1-(-2p+2)³/2`. */
function easeInOutCubic(p: number): number {
  return p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2;
}

/* ------------------------------------------------------------------ *
 * Section wrapper
 * ------------------------------------------------------------------ */

export interface ReportShellSectionProps {
  id: ReportSectionId;
  /**
   * The `.rv-h2` section title — normally the `REPORT_SECTIONS` label. Omit
   * for a section that titles itself: the design's `sec("overview", null, …)`
   * passes no title because the Overview leads with the document's h1
   * headline. An untitled section names itself with `aria-label` instead.
   */
  title?: string;
  /** Used as the `aria-label` when `title` is omitted. */
  label?: string;
  children: ReactNode;
}

/**
 * `.rv-sec` — one section of the document, with its `.rv-h2` heading.
 *
 * None of the section components renders its own `.rv-h2`; the design's
 * `sec(id, title, children)` helper owns it, and so does this. The
 * `data-report-section` attribute is how `ReportShell` finds the section to
 * observe and to scroll to, and `id="sec-<id>"` matches the design's own
 * anchor so a `#sec-figures` link keeps working.
 */
export function ReportShellSection({
  id,
  title,
  label,
  children,
}: ReportShellSectionProps) {
  return (
    <section
      id={`sec-${id}`}
      data-report-section={id}
      aria-label={title ? undefined : label}
      className="mb-[52px] font-[family-name:var(--font-body)]"
    >
      {/* `!` — see `ReportSectionHeading`: unlayered `h2` rules in
          `tokens.css` outrank Tailwind's `@layer utilities`. */}
      {title && (
        <h2 className="m-0 mb-4 text-[19px]! font-bold leading-[1.3]! tracking-[-0.02em]! text-(--fg-primary)">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Shell
 * ------------------------------------------------------------------ */

/**
 * The scrolling report document with its scroll-spying rail.
 *
 * **Scroll-spy.** One `IntersectionObserver` over a thin band near the top of
 * the scroll container (`rootMargin: "-76px 0px -55% 0px"`), and the active
 * section is the **first intersecting one in `sections` order**. Nothing is
 * sorted by `intersectionRatio`: with eight sections of wildly different
 * heights, ratio always elects the tallest section on screen and a short one
 * beside it never becomes active (plan §6 risk 3). Document order plus a
 * narrow band reproduces the design's own rule — "the last section whose top
 * has passed the reading line" — and gives every section a turn proportional
 * to its height.
 *
 * A second observer watches a tail sentinel so that reaching the end of the
 * document always elects the last section, which the band alone cannot do
 * when the last section is too short to reach the reading line.
 *
 * Programmatic scrolls suppress the spy for exactly as long as the animation
 * runs, rather than for a fixed timeout — this shell owns the easing, so it
 * knows when the scroll has landed.
 */
export function ReportShell({
  sections,
  activeSectionId,
  onSelectSection,
  locked,
  children,
}: ReportShellProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const tailRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  /** True while an owned scroll animation runs; the spy stays quiet. */
  const suppressSpyRef = useRef(false);
  /** The section the document is actually showing, spy or scroll alike. */
  const currentIdRef = useRef<ReportSectionId>(activeSectionId);

  // Read through refs so the observer effect never re-subscribes on a new
  // callback identity — re-observing on every render would drop the
  // intersection state the spy is built on.
  const onSelectSectionRef = useRef(onSelectSection);
  onSelectSectionRef.current = onSelectSection;

  const cancelAnimation = useCallback(() => {
    if (frameRef.current === null) return;
    cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  const scrollToSection = useCallback(
    (id: ReportSectionId) => {
      const scrollEl = scrollRef.current;
      const target = scrollEl?.querySelector<HTMLElement>(
        `[${SECTION_ATTR}="${id}"]`,
      );
      if (!scrollEl || !target) return;

      currentIdRef.current = id;
      cancelAnimation();

      const raw =
        scrollEl.scrollTop +
        target.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top -
        SECTION_TOP_GAP_PX;
      const to = Math.max(
        0,
        Math.min(raw, scrollEl.scrollHeight - scrollEl.clientHeight),
      );
      const from = scrollEl.scrollTop;
      const distance = to - from;

      // The spy must not fight the sections streaming past during the
      // animation, so it is quiet until this lands.
      suppressSpyRef.current = true;
      const settle = () => {
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          suppressSpyRef.current = false;
        });
      };

      if (Math.abs(distance) < 2 || prefersReducedMotion()) {
        scrollEl.scrollTop = to;
        settle();
        return;
      }

      const startedAt = performance.now();
      const step = () => {
        const progress = Math.min(
          1,
          (performance.now() - startedAt) / SCROLL_DURATION_MS,
        );
        scrollEl.scrollTop = from + distance * easeInOutCubic(progress);
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(step);
          return;
        }
        frameRef.current = null;
        settle();
      };
      frameRef.current = requestAnimationFrame(step);
    },
    [cancelAnimation],
  );

  useEffect(() => cancelAnimation, [cancelAnimation]);

  /* ---- scroll-spy ---- */
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    if (typeof window.IntersectionObserver === "undefined") return;

    const visible = new Set<ReportSectionId>();
    let atEnd = false;

    const recompute = () => {
      if (suppressSpyRef.current) return;
      const last = sections.at(-1);
      // Reaching the tail always means the last section, however short it is.
      const candidate =
        atEnd && last
          ? last.id
          : sections.find((section) => visible.has(section.id))?.id;
      if (!candidate || candidate === currentIdRef.current) return;
      currentIdRef.current = candidate;
      onSelectSectionRef.current(candidate);
    };

    const bandObserver = new window.IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset.reportSection as
            | ReportSectionId
            | undefined;
          if (!id) continue;
          if (entry.isIntersecting) {
            visible.add(id);
          } else {
            visible.delete(id);
          }
        }
        recompute();
      },
      {
        root: scrollEl,
        rootMargin: `-${SPY_TOP_INSET_PX}px 0px -${SPY_BOTTOM_INSET_PCT}% 0px`,
        threshold: 0,
      },
    );
    for (const element of scrollEl.querySelectorAll<HTMLElement>(
      `[${SECTION_ATTR}]`,
    )) {
      bandObserver.observe(element);
    }

    const tailObserver = new window.IntersectionObserver(
      (entries) => {
        const entry = entries.at(-1);
        if (!entry) return;
        // A document short enough to fit shows its tail from the top, where
        // the last section is emphatically not what the reader is on.
        const scrollable = scrollEl.scrollHeight > scrollEl.clientHeight + 4;
        atEnd = entry.isIntersecting && scrollable;
        recompute();
      },
      { root: scrollEl, threshold: 0 },
    );
    const tail = tailRef.current;
    if (tail) tailObserver.observe(tail);

    return () => {
      bandObserver.disconnect();
      tailObserver.disconnect();
    };
    // Re-subscribes when `sections` changes, which is when the observed
    // elements change — so pass a referentially stable array
    // (`REPORT_SECTIONS`), not a fresh literal on every render.
  }, [sections]);

  /* ---- external navigation ---- */
  useEffect(() => {
    if (activeSectionId === currentIdRef.current) return;
    scrollToSection(activeSectionId);
  }, [activeSectionId, scrollToSection]);

  return (
    // `.rv`
    <div className="relative flex h-full min-h-0 bg-(--bg-canvas)">
      {/* `.rv-toc` */}
      <nav
        aria-label="Report sections"
        className={[
          "flex flex-[0_0_188px] flex-col gap-0.5 max-[1080px]:flex-[0_0_160px]",
          "overflow-y-auto border-r border-(--border-subtle) px-3.5 py-6",
        ].join(" ")}
      >
        {/* `.rv-toclbl` */}
        <div className="mb-2.5 flex items-center gap-[7px] px-2.5 text-[11px] font-bold uppercase tracking-[0.1em] text-(--fg-muted)">
          Report
        </div>
        {sections.map((section) => {
          const active = section.id === activeSectionId;
          return (
            <button
              key={section.id}
              type="button"
              // A table of contents marks the reader's place in one document,
              // which is `location`, not `page`.
              aria-current={active ? "location" : undefined}
              onClick={() => {
                // Scroll first: this stamps `currentIdRef`, so the external
                // navigation effect below sees the move already made and does
                // not run a second animation. It also re-scrolls when the
                // clicked item is already active, which the design gets from
                // its `sectionNonce`.
                scrollToSection(section.id);
                onSelectSection(section.id);
              }}
              className={[
                "cursor-pointer border-0 border-l-2 bg-transparent px-2.5 py-[7px] text-left",
                "font-[family-name:var(--font-body)] text-[13px]",
                "transition-all duration-[140ms] ease-(--ease)",
                active
                  ? "border-l-(--fg-brand) font-bold text-(--fg-brand)"
                  : "border-l-transparent font-medium text-(--fg-muted) hover:text-(--fg-secondary)",
              ].join(" ")}
            >
              {section.label}
            </button>
          );
        })}
        {locked && (
          <p className="mt-3 mb-0 px-2.5 text-[11.5px] leading-[1.45] text-(--fg-muted)">
            {LOCKED_CAPTION}
          </p>
        )}
      </nav>

      {/* `.rv-scroll` */}
      <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto">
        {/* `.rv-body` */}
        {/* `.rv-body`. The design sets `padding: 30px 32px 60px`; the top is
            deliberately 48px here. The design canvas is a standalone page, but in
            the app this column starts flush against the window with no chrome
            above it, and 30px leaves the header badges cramped against the top
            edge. Keep this in step with `Intake.tsx`'s report scroller. */}
        <div className="mx-auto max-w-[760px] px-8 pt-[48px] pb-[60px]">
          {children}
          {/* `.rv-tail` — also the sentinel that elects the last section. */}
          <div ref={tailRef} aria-hidden="true" className="h-10" />
        </div>
      </div>
    </div>
  );
}
